import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Grid, ToggleButton, ToggleButtonGroup, IconButton, Tooltip, CircularProgress, Alert } from '@mui/material';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { card, th, td, EmptyState } from '../uiKit';
import http, { apiErrorMessage } from '../../Api/http';
import { GetEmployeePaymentHistory } from '../../Api/Api';
import { inr, fmtDate, parseApiDate } from '../../utils/format';

// White rows; a coloured bar down the left edge says what each line is.
const KIND = {
    Prepaid: { bar: '#2F8F5F', fg: '#2F8F5F', label: 'Prepaid' },
    Expense: { bar: '#F59E0B', fg: '#B45309', label: 'Expense' },
    Postpaid: { bar: '#3B82C4', fg: '#3B82C4', label: 'Postpaid' },
};
const GRID = '120px 120px 1fr 110px 130px 44px';
const FILTERS = [['All', 'All'], ['Expenses', 'Expenses'], ['Prepaid', 'Prepaid'], ['Postpaid', 'Postpaid']];

// Balances arrive signed: negative while the company is owed nothing and the employee is out of pocket
// ("due"), positive while paid ahead ("advance"). Show the magnitude beside the status word.
export const balanceTone = (status) => (status === 'due' ? '#C2475A' : status === 'advance' ? '#2F8F5F' : '#64748B');
export const balanceWord = (status) => (status === 'due' ? 'due' : status === 'advance' ? 'advance' : 'settled');

const toDate = (v) => { const d = parseApiDate(v); return d ? fmtDate(d) : '—'; };
const num = (v) => Number(v) || 0;

const toneKey = (e) => (e === 'Expense' ? 'Expense' : e === 'Postpaid' ? 'Postpaid' : 'Prepaid');

const toggleSx = {
    '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 2, borderRadius: '7px !important', mr: 1, border: '1px solid #E5E7EB !important', bgcolor: '#fff' },
    '& .Mui-selected': { bgcolor: '#EFECFE !important', color: '#6B58D6' },
};

/**
 * One employee's shoot ledger, oldest to newest, straight from GetEmployeePaymentHistory: their shoot
 * expenses and Prepaid / Postpaid payments merged, each with the running balance after that line.
 * The filter narrows the list only; the cards always show everything. `refreshKey` re-reads it when the
 * parent's data changes.
 */
export default function PaymentLedger({ code, onOpenEntry, onRemoveExpense, onRemovePayment, refreshKey }) {
    const [filter, setFilter] = useState('All');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetEmployeePaymentHistory, { params: { employeeCode: code, filter } });
            if (body?.error) throw new Error(body.message || 'Could not load the payment history.');
            setData(body?.data ?? body ?? {});
            setError('');
        } catch (err) {
            if (err?.response?.status === 404) { setData({}); setError(''); } else setError(err?.response ? apiErrorMessage(err, 'Could not load the payment history.') : (err.message || 'Could not load the payment history.'));
        } finally {
            setLoading(false);
        }
    }, [code, filter]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const entries = Array.isArray(data?.entries) ? data.entries : [];
    const status = data?.balanceStatus;
    const remove = async (e) => {
        try {
            if (e.entryType === 'Expense') await onRemoveExpense(e.id); else await onRemovePayment(e.id);
            await load();
        } catch (err) {
            setError(err.message || 'Could not delete that entry.');
        }
    };

    return (
        <>
            <ToggleButtonGroup exclusive size="small" value={filter} onChange={(_, v) => v && setFilter(v)} sx={{ ...toggleSx, mb: 2.5 }}>
                {FILTERS.map(([v, l]) => <ToggleButton key={v} value={v}>{l}</ToggleButton>)}
            </ToggleButtonGroup>

            {error && <Alert severity="error" sx={{ mb: 2 }} action={<Typography onClick={load} sx={{ cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Retry</Typography>}>{error}</Alert>}

            <Box sx={{ ...card, mb: 2.5, p: 2 }}>
                <Grid container spacing={2}>
                    {[['Expense total', inr(num(data?.expenseTotal)), '#B45309'],
                        ['Prepaid paid', inr(num(data?.prepaidPaid)), '#2F8F5F'],
                        ['Postpaid paid', inr(num(data?.postpaidPaid)), '#3B82C4'],
                        [status === 'advance' ? 'Advance left' : 'Balance due', inr(Math.abs(num(data?.balanceDue))), balanceTone(status)]].map(([label, val, color]) => (
                        <Grid key={label} size={{ xs: 6, md: 3 }}>
                            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#98A0AE', letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</Typography>
                            <Typography sx={{ fontSize: 18, fontWeight: 800, color, mt: 0.3 }}>{val}</Typography>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Box sx={{ ...card, overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 700 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: GRID, bgcolor: '#F7F6FD' }}>
                            {['DATE', 'TYPE', 'DETAILS', 'AMOUNT', 'BALANCE', ''].map((h, i) => <Box key={i} sx={th}>{h}</Box>)}
                        </Box>
                        {loading && !data ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
                        ) : entries.length === 0 ? (
                            <EmptyState icon={ReceiptLongRoundedIcon} title="No payment history yet" hint="Shoot expenses and the payments made to this employee appear here by date." />
                        ) : entries.map((e, i) => {
                            const k = KIND[toneKey(e.entryType)];
                            const isExpense = e.entryType === 'Expense';
                            return (
                                <Box
                                    key={`${e.entryType}-${e.id ?? i}`} onClick={() => isExpense && onOpenEntry && onOpenEntry(e.id)}
                                    sx={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', cursor: isExpense ? 'pointer' : 'default', bgcolor: '#fff', borderLeft: `6px solid ${k.bar}`, borderBottom: '1px solid #EEF0F6', '&:hover': { bgcolor: '#FAFAFE' } }}
                                >
                                    <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13, fontWeight: 700 }}>{toDate(e.date)}</Typography></Box>
                                    <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13, fontWeight: 800, color: k.fg }}>{k.label}</Typography></Box>
                                    <Box sx={{ ...td, borderBottom: 'none', minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{e.details || '—'}</Typography>
                                        <Typography sx={{ fontSize: 11, color: '#64748B' }} noWrap>{e.meta}</Typography>
                                    </Box>
                                    <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13, fontWeight: 800, color: k.fg }}>{inr(num(e.amount))}</Typography></Box>
                                    <Box sx={{ ...td, borderBottom: 'none' }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: balanceTone(e.balanceStatus) }}>{inr(Math.abs(num(e.balance)))}</Typography>
                                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: balanceTone(e.balanceStatus) }}>{balanceWord(e.balanceStatus)}</Typography>
                                    </Box>
                                    <Box sx={{ ...td, borderBottom: 'none', px: 0.5 }}>
                                        <Tooltip title={isExpense ? 'Delete expense' : 'Remove payment'}>
                                            <IconButton size="small" onClick={(ev) => { ev.stopPropagation(); remove(e); }}><DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </>
    );
}
